// Client-side image compression for the album upload path — w50.1 §5.
//
// Native Canvas only, zero dependencies. The spec's own survey (§5.1) priced
// `compressorjs` at 15.7 KB gzip and `browser-image-compression` at 19.6 KB to
// do what Canvas already does, so pulling one in would spend bundle on a
// problem this file does not have and break AGENTS.md §3 #13 ("Reuse before
// create") on top of it.
//
// ---------------------------------------------------------------------------
// EXIF is dropped, and that is not an implementation detail
// ---------------------------------------------------------------------------
// `ctx.drawImage` + `canvas.toBlob` produces a brand-new file with no EXIF
// segment — `DateTimeOriginal` and GPS alike. The backend reads that tag to
// fill `album_items.taken_at` (`LocalFSBackend.extract_taken_at`,
// `backend/app/modules/album/storage.py:105`), and `taken_at` is what the
// album grid sorts by. So every *compressed* photo sorts by upload time
// instead of capture time. w50.1 §5.4 left this trade for the user to call;
// this round accepts it, and this module is written so it cannot be mistaken
// for a no-op: `CompressionResult.compressed === true` is exactly the set of
// files that reached disk without their capture date.
//
// HEIC does not have that problem, and does not get it. Safari *can* decode
// HEIC, so without an explicit guard this file would happily re-encode the
// iPhone originals that the backend went out of its way to whitelist
// (`ALLOWED_EXTENSIONS[".heic"]`, storage.py:25) — the exact photos whose
// EXIF matters most. `isCompressible` returns false for them up front, so
// they pass through byte-for-byte. Deleting that one guard is all it would
// take to exploit Safari's HEIC decode later.

// w50.1 §5.3: the longest edge is capped at 1920 and quality at 0.82. Below
// 0.7 the artefacts are visible; above ~0.9 the byte savings are not worth
// the re-encode.
const MAX_LONG_EDGE = 1920;
const QUALITY = 0.82;

/** §5.3: "已夠小的圖 < 200KB 直接放行 — 不做無意義的有損重編". */
const SKIP_BELOW_BYTES = 200 * 1024;

/**
 * Output formats, best-first (§5.3 probes AVIF → WebP → JPEG and takes the
 * first `toBlob` that returns a Blob).
 *
 * `sendable: false` is a backend fact, not a preference. The upload path
 * derives the stored MIME from the **filename extension**
 * (`backend/app/modules/album/service/media.py:106-109`) and rejects anything
 * outside `ALLOWED_EXTENSIONS` (storage.py:21-29), which lists `.jpg` `.jpeg`
 * `.png` `.heic` `.webp` `.mp4` `.mov` — and no `.avif`. Encoding AVIF would
 * therefore turn every upload into a 400 the moment the browser advertised
 * support for it, which is the opposite of the spec's intent. AVIF stays in
 * the list, in its preferred position, with the switch that turns it on: add
 * `".avif": "image/avif"` to `ALLOWED_EXTENSIONS` and flip this flag.
 */
interface Encoding {
  mime: string;
  ext: string;
  sendable: boolean;
}

const ENCODINGS: readonly Encoding[] = [
  { mime: "image/avif", ext: ".avif", sendable: false },
  { mime: "image/webp", ext: ".webp", sendable: true },
  // The last entry doubles as the fallback §5.2 asks for: every mainstream
  // engine has encoded JPEG since forever, so the loop cannot come up empty.
  { mime: "image/jpeg", ext: ".jpg", sendable: true },
];

/** Extensions Canvas can decode, and that this module is willing to touch. */
const DECODABLE_EXT = /\.(jpe?g|png|webp|bmp|avif)$/i;

/**
 * Formats that pass through untouched.
 *
 * HEIC/HEIF: see the header — the backend accepts them and Safari is the one
 * engine that could decode them, which is exactly why we decline to.
 *
 * GIF is here for a different reason: Canvas decodes only the first frame, so
 * "compressing" an animated GIF would silently freeze it.
 */
const PASSTHROUGH_EXT = /\.(heic|heif|gif)$/i;

export interface CompressionResult {
  /** What to upload. The original `File` instance when nothing was compressed. */
  file: File;
  /** Bytes before compression. */
  originalBytes: number;
  /** Bytes of `file`. Equals `originalBytes` unless `compressed`. */
  bytes: number;
  /** True only when `file` is a Canvas re-encode — and so has lost its EXIF. */
  compressed: boolean;
}

function passthrough(file: File): CompressionResult {
  return { file, originalBytes: file.size, bytes: file.size, compressed: false };
}

/**
 * Compress one picked file for upload. Never throws and never returns a file
 * larger than it was given — §5.2's two "永遠不許" rules. A HEIC, a video, a
 * corrupt file, a browser with no Canvas: all come back as the original.
 */
export async function compressImage(file: File): Promise<CompressionResult> {
  if (file.size < SKIP_BELOW_BYTES) return passthrough(file);
  if (!isCompressible(file)) return passthrough(file);
  try {
    const decoded = await decode(file);
    if (!decoded) return passthrough(file);
    try {
      const { width, height } = fit(decoded.width, decoded.height);
      const encoded = await encode(decoded.source, width, height);
      if (!encoded) return passthrough(file);
      // §5.2: 壓縮後比原檔更大要保留原檔. A PNG re-encoded as JPEG can land
      // above the original, and so can any file the encoder handles badly.
      if (encoded.blob.size >= file.size) return passthrough(file);
      const out = new File([encoded.blob], rename(file.name, encoded.ext), {
        type: encoded.mime,
        lastModified: file.lastModified,
      });
      return { file: out, originalBytes: file.size, bytes: out.size, compressed: true };
    } finally {
      decoded.release();
    }
  } catch {
    // §5.2: 壓縮失敗就整個上傳失敗 is forbidden — the upload is the user's
    // actual goal and the compression is only there to help it along.
    return passthrough(file);
  }
}

function isCompressible(file: File): boolean {
  if (PASSTHROUGH_EXT.test(file.name)) return false;
  // A declared MIME is the stronger signal, but browsers hand out an empty
  // `type` for anything they do not recognise (HEIC on Chrome, for one), so
  // the filename has to be able to answer on its own.
  if (file.type) return file.type.startsWith("image/");
  return DECODABLE_EXT.test(file.name);
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  /** Free whatever the decoder allocated. A no-op for the <img> path. */
  release: () => void;
}

async function decode(file: File): Promise<Decoded | null> {
  if (typeof createImageBitmap === "function") {
    try {
      // `imageOrientation: "from-image"` applies the EXIF orientation tag
      // before we draw. Without it an iPhone portrait JPEG (orientation 6)
      // is stored sideways, and this function would be the thing that broke it.
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      // Some engines ship `createImageBitmap` but reject the options bag.
      // Fall through to the element path rather than giving up on the file.
    }
  }
  return decodeViaElement(file);
}

async function decodeViaElement(file: File): Promise<Decoded | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    if (typeof img.decode === "function") await img.decode();
    else await loaded(img);
    if (!img.naturalWidth || !img.naturalHeight) return null;
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => {},
    };
  } catch {
    // A format this engine cannot decode lands here. That is the expected
    // path for HEIC on Chrome and Android, not an error worth surfacing.
    return null;
  } finally {
    // Safe even though the caller goes on to draw: revoking drops the URL,
    // not the pixels an already-loaded <img> is holding.
    URL.revokeObjectURL(url);
  }
}

function loaded(img: HTMLImageElement): Promise<void> {
  return new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("image decode failed"));
  });
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

/** Scale to fit `MAX_LONG_EDGE`, never up — §5.3 caps the long edge, it does not mandate it. */
function fit(width: number, height: number): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= MAX_LONG_EDGE) return { width, height };
  const scale = MAX_LONG_EDGE / long;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function encode(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<{ blob: Blob; mime: string; ext: string } | null> {
  for (const enc of ENCODINGS) {
    if (!enc.sendable) continue;
    const blob = await toBlob(source, width, height, enc.mime);
    // `toBlob` answers `null` for a type it cannot encode — that is the probe.
    if (blob && blob.size > 0) return { blob, mime: enc.mime, ext: enc.ext };
  }
  return null;
}

function toBlob(
  source: CanvasImageSource,
  width: number,
  height: number,
  mime: string,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve(null);
      return;
    }
    // JPEG has no alpha channel. A transparent PNG drawn onto the default
    // transparent canvas encodes those pixels as black, so flatten onto white
    // first — the format we actually prefer (WebP) keeps its alpha intact and
    // never reaches this branch on a browser that supports both.
    if (mime === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(source, 0, 0, width, height);
    try {
      canvas.toBlob((blob) => resolve(blob), mime, QUALITY);
    } catch {
      // One unsupported candidate must not end the probe: the next format
      // in the list may well encode fine.
      resolve(null);
    }
  });
}

/** `IMG_0042.JPG` + `.webp` → `IMG_0042.webp`. The extension has to travel with the bytes. */
function rename(name: string, ext: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem}${ext}`;
}